/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, alpha, Switch, FormControlLabel,
    IconButton, Fade, useMediaQuery, Chip,
    Stack, Tooltip, CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { useDispatch, useSelector } from 'src/redux/store';
import { getConsentForms } from 'src/redux/slices/consentV2';
import { callApi } from 'src/utils/apiCall';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import uuidv4 from 'src/utils/uuidv4';
import { buildResolvedDomainUrl } from 'src/utils/resolvedDomainUrl';
import { common } from 'src/theme/palette';

import PatientSelector from './PatientSelector';
import MultiPatientPicker from './MultiPatientPicker';
import TreatmentSelector from './TreatmentSelector';
import QuickSend from './QuickSend';
// SmartSuggestions removed per user feedback (FE-8). Component file kept
// alive on disk so the suggestion engine logic isn't lost — re-enable by
// re-adding this import + the previous render in Section 2.
// import SmartSuggestions from './SmartSuggestions';
import SequencePresetManager from './SequencePresetManager';
import ChannelComposer from './ChannelComposer';
import SendFormsPreview from './SendFormsPreview';
import PracticeFieldsBlock from './PracticeFieldsBlock';
import AdvancedReveal from './AdvancedReveal';
import SingleFormPicker from './SingleFormPicker';
import SendSettingsStrip from './SendSettingsStrip';
import AdvancedDispatchBar from './AdvancedDispatchBar';
import BroadcastAttestationCard, { isAttestationValid } from './BroadcastAttestationCard';
import { buildSendFormsPayload } from './quickSendUtils';

// FE-4: the wizard is now a single-screen layout — no step machine.
// We keep the `STEPS` constant as a one-element list for the progress
// bar / header pill at the top, but there's only one stop.
const STEPS = ['Send'];

const DEFAULT_EXPIRY_HOURS = 168;

/**
 * SendFormsDialog — 2-step wizard.
 *
 *   Step 1 — Compose: 3-section left column (Audience → Forms → Channel &
 *            Message) and a sticky preview rail on the right.
 *   Step 2 — Review & Send: trimmed three-card summary; the final CTA POSTs
 *            to /consent-requests/send-forms/.
 *
 * IMPORTANT: ConsentRequest creation is deferred until the final
 * "Send via {channel}" CTA in Step 2. No POST happens during preview,
 * navigation, or step changes.
 */
const SendFormsDialog = ({ open, onClose, practiceId, initialLead, initialForm, onSuccess }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { enqueueSnackbar } = useCustomSnackbar();

    const { forms, formsLoading } = useSelector((state) => state.consentV2);
    const practiceTreatmentData = useSelector((state) => state.practiceTreatment?.practiceTreatment);
    const practice = useSelector((state) => state.practice?.practice);

    // Flatten practice-level treatments
    const practiceTreatments = useMemo(() => {
        const raw = practiceTreatmentData?.results || [];
        return raw
            .filter((t) => t?.enabled !== false)
            .map((t) => ({
                id: t.id,
                name: t.treatment_item?.name || '',
                category: t.treatment_item?.treatment_category?.name || '',
                treatment_item_id: t.treatment_item?.id,
            }))
            .filter((t) => t.name)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [practiceTreatmentData]);

    // ── State ────────────────────────────────────────────────────────────────
    // viewMode — 'QUICK' renders the minimalist 4-step wizard; 'ADVANCED'
    // is the power-user dense layout. Quick is ALWAYS the default on open
    // (May 2026 product call: new users should land in the simpler flow
    // every time; Advanced is opt-in per session). We intentionally do NOT
    // hydrate from localStorage so the dialog can't open in Advanced just
    // because the last visit ended there.
    const [viewMode, setViewMode] = useState('QUICK');
    // Reset to QUICK whenever the dialog re-opens, even if the user
    // switched to Advanced in a previous session within the same SPA load.
    useEffect(() => {
        if (open) setViewMode('QUICK');
    }, [open]);
    const switchToAdvanced = useCallback(() => setViewMode('ADVANCED'), []);

    const [audienceMode, setAudienceMode] = useState('ONE_PATIENT');
    // FE-4: the wizard is single-screen — `activeStep` is kept as a constant
    // 0 so the progress bar / Fade transitions don't need ripping out.
    const activeStep = 0;

    // ONE_PATIENT
    const [selectedLead, setSelectedLead] = useState(initialLead || null);

    // PATIENT_LIST
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [selectedSegment, setSelectedSegment] = useState(null);
    // Legacy boolean attestation removed — see ``attestation`` state below
    // for the new lawful_basis + source_description shape that the BE
    // actually accepts (broadcast_attestation in send_forms_serializers).

    const [selectedForms, setSelectedForms] = useState([]);
    const [selectedTreatmentIds, setSelectedTreatmentIds] = useState([]);
    const [extraTreatmentIds, setExtraTreatmentIds] = useState([]);
    const [consentConfig, setConsentConfig] = useState(null);
    const [sequencePresets, setSequencePresets] = useState([]);
    const [bundleMode, setBundleMode] = useState(true);
    const [presetManagerOpen, setPresetManagerOpen] = useState(false);
    const [presetManagerView, setPresetManagerView] = useState('list');
    const [activePresetId, setActivePresetId] = useState(null);
    const [presetSnapshot, setPresetSnapshot] = useState(null);

    // Expiry — `customHours` is used when expiryHours === 'custom'
    const [expiryHours, setExpiryHours] = useState(DEFAULT_EXPIRY_HOURS);
    const [customHours, setCustomHours] = useState(48);

    // Channel & message
    // 2026-04-26: switched from single ``channel`` (string) to ``channels``
    // (array). Practice users can now combine channels — e.g. send the
    // form via Email AND WhatsApp simultaneously, or generate a Copy link
    // alongside an outbound delivery. ``primaryChannel`` (channels[0]) is
    // used wherever we still need a single value (right-rail preview
    // headline, snackbar text). The audience-mode matrix continues to gate
    // which channels are even ALLOWED to appear together.
    const [channels, setChannels] = useState(['EMAIL']);
    const primaryChannel = channels[0] || 'EMAIL';
    const channel = primaryChannel; // back-compat alias for non-refactored consumers
    const setChannel = (next) => setChannels([next]); // back-compat shim
    const hasEmail = channels.includes('EMAIL');
    const hasWhatsApp = channels.includes('WHATSAPP');
    const [emailTemplateId, setEmailTemplateId] = useState(null);
    const [whatsappTemplateId, setWhatsappTemplateId] = useState(null);
    const [emailTemplate, setEmailTemplate] = useState(null);
    const [whatsappTemplate, setWhatsappTemplate] = useState(null);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [whatsappTemplates, setWhatsappTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    // BCC
    const [bccEnabled, setBccEnabled] = useState(false);
    const [bccList, setBccList] = useState([]); // string[]

    // ── Inline TemplateCard edits (subject/body overrides) ──────────────────
    // When the form-type-aware TemplateCard is rendered, it owns the active
    // template + per-channel subject/body edits. We mirror those here so the
    // sticky preview rail stays in sync without refetching.
    const [inlineEmailDraft, setInlineEmailDraft] = useState(null); // { subject, body } | null
    const [inlineWhatsappDraft, setInlineWhatsappDraft] = useState(null); // { body } | null

    // Sending
    const [sending, setSending] = useState(false);

    // ─── Post-process dispatch (REVAMP) ─────────────────────────────────────
    // Architectural alignment with QuickSend (May 2026): the Advanced view
    // now mirrors the "link first, dispatch later" pattern.
    //
    //   1. User configures audience + forms + channels + practice fields.
    //   2. Clicks ``Generate link`` → POSTs ``send-forms`` with
    //      ``channels=['COPY_LINK']`` + ``idempotency_key``. The BE
    //      creates one ConsentRequest per (lead × form) and returns the
    //      access_token. ``sendResult.links[i].consentRequestId`` is
    //      captured for reuse.
    //   3. For each outbound channel (EMAIL / WHATSAPP) the user wants to
    //      fire, click that channel pill in the post-flight bar →
    //      ``send-forms`` POST again with ``consent_request_ids`` set so
    //      the BE skips ``ConsentRequest.objects.create`` and dispatches
    //      via the existing CRs. ONE access_token, multiple deliveries.
    //
    // Why this matters: the legacy single-shot ``handleFinalSend`` POSTed
    // ALL channels at once, which meant a multi-channel combo created
    // multiple CRs with different access_tokens — the patient received
    // multiple links and the Form Submissions list showed multiple
    // pending rows for one logical send. This rewire fixes that.
    const [generating, setGenerating] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4());
    // Per-channel send status — IDLE → SENDING → SENT | FAILED. Drives
    // the AdvancedDispatchBar pill colours.
    const [channelStatus, setChannelStatus] = useState({
        EMAIL: 'IDLE', WHATSAPP: 'IDLE',
    });

    // ─── GDPR attestation (PATIENT_LIST mode) ──────────────────────────────
    // Replaces the boolean ``broadcastAttested`` switch. The legacy switch
    // sent ``{user_confirmed, confirmed_at}`` to the BE, which
    // SendFormsRequestSerializer rejects: it requires ``{lawful_basis,
    // source_description}`` (see validate_broadcast_attestation). Every
    // PATIENT_LIST send via Advanced was 400-ing on a quiet field-level
    // error that didn't surface in the snackbar.
    const [attestation, setAttestation] = useState({
        lawful_basis: '', source_description: '',
    });

    // ─── Per-form overrides ────────────────────────────────────────────────
    // Maps keyed by form_id. When populated, the BE merges these on top of
    // the top-level treatment_ids / practice_field_values so each form's
    // PDF can carry its own context. Backward-compatible: empty maps are
    // omitted from the payload entirely.
    const [perFormTreatmentIds, setPerFormTreatmentIds] = useState({});
    const [perFormPracticeFieldValues, setPerFormPracticeFieldValues] = useState({});

    // ─── Recurrence (rrule-lite) ───────────────────────────────────────────
    // Object: { frequency: 'WEEKLY'|'MONTHLY', interval, count }. Only
    // applied when scheduleMode === 'LATER' AND audience is ONE_PATIENT
    // (same gating as scheduled_at on the BE). BE fans out into ``count``
    // scheduled CRs each at scheduled_at + i × interval × frequency.
    const [recurrence, setRecurrence] = useState(null);

    // FE-2: Practice field values dict — { "system:treated_by": {...},
    // "<practice_form_field_id>": {...} }. Owned here so the wizard can
    // ship it on Send AND mirror it into the preview panel.
    const [practiceFieldValues, setPracticeFieldValues] = useState({});

    // FE-5: Inline-edit drafts from the preview panel's contenteditable
    // areas. The preview emits { subject, body, whatsappBody } partials
    // via onTemplateChange and we merge them in.
    const [previewTemplateDraft, setPreviewTemplateDraft] = useState(null);
    const handlePreviewTemplateChange = useCallback((patch) => {
        setPreviewTemplateDraft((prev) => ({ ...(prev || {}), ...patch }));
    }, []);

    // FE-4 / FE-7: schedule mode + send-at timestamp + link-expiry (days).
    // Surfaced in SendSettingsStrip directly above the wizard footer.
    const [scheduleMode, setScheduleMode] = useState('NOW');
    const [scheduledAt, setScheduledAt] = useState('');
    const expiryDays = useMemo(() => {
        const hrs = expiryHours === 'custom' ? Math.max(1, Number(customHours) || 1) : Number(expiryHours);
        return Math.max(1, Math.round(hrs / 24));
    }, [expiryHours, customHours]);
    const setExpiryDays = useCallback((days) => {
        setExpiryHours(Math.max(1, Number(days) || 1) * 24);
    }, []);

    // ── Computed ─────────────────────────────────────────────────────────────
    const isAnonymous = audienceMode === 'ANONYMOUS';
    const isOnePatient = audienceMode === 'ONE_PATIENT';
    const isPatientList = audienceMode === 'PATIENT_LIST';
    const isSequence = bundleMode && selectedForms.length > 1 && isOnePatient;
    const resolvedExpiryHours = expiryHours === 'custom' ? Math.max(1, Number(customHours) || 1) : Number(expiryHours);

    const patientName = `${selectedLead?.first_name || ''} ${selectedLead?.last_name || ''}`.trim() || 'Patient';
    const patientEmail = selectedLead?.lead_contact?.email || selectedLead?.email || '';
    const patientPhone = selectedLead?.lead_contact?.phone || selectedLead?.phone || '';
    const practiceName = practice?.name || practice?.practice_name || '';

    // ── Derived: patient's active treatments ────────────────────────────────
    const patientTreatments = useMemo(() => {
        if (!selectedLead?.lead_treatments) return [];
        return selectedLead.lead_treatments.filter(
            (t) => t.status !== 'CLOSED' && t.status !== 'ARCHIVED'
        );
    }, [selectedLead]);

    // ── Initial-lead handling ───────────────────────────────────────────────
    useEffect(() => {
        if (open && initialLead) {
            setSelectedLead(initialLead);
            setAudienceMode('ONE_PATIENT');
        }
    }, [open, initialLead]);

    useEffect(() => {
        if (open && initialForm) {
            setSelectedForms([initialForm]);
        }
    }, [open, initialForm]);

    // ── Auto-select all active treatments when patient changes ──────────────
    useEffect(() => {
        if (patientTreatments.length > 0) {
            setSelectedTreatmentIds(patientTreatments.map((t) => t.id));
        } else {
            setSelectedTreatmentIds([]);
        }
        setExtraTreatmentIds([]);
    }, [patientTreatments]);

    // ── Load forms, consent config, presets, templates ──────────────────────
    useEffect(() => {
        if (!open || !practiceId) return;

        // Perf + UX: the wizard renders form name / type / section count
        // and a quick-preview drawer that needs description + section
        // names. Pass an explicit ``fields=`` projection so the BE
        // ``PracticeFormViewSet`` skips the heavy treatments /
        // conditional_logics / sections__fields prefetch graph —
        // ``sections{id,name}`` keeps the section-row prefetch but NOT
        // the deep ``sections__fields__field_type`` chain (BE perf pass
        // 2026-04-26). Also filter out ``is_template=true`` rows: the
        // wizard can't send a template form to a patient, and rendering
        // them in the picker just adds noise + load time.
        dispatch(getConsentForms(practiceId, {
            limit: 200,
            isTemplate: false,
            fields: '{id,name,form_type,description,is_template,is_default,sections{id,name}}',
        }));

        // Consent / form-URL config
        callApi(`/api/v1/practices/${practiceId}/choices/?ordering=-created_at`)
            .then((res) => {
                if (res.status === 200 && res.data?.results) {
                    const config = res.data.results.find(
                        (c) => c?.metadata?.type === 'consent_form_config'
                    );
                    setConsentConfig(config || null);
                }
            })
            .catch(() => {});

        // Form sequence presets
        loadPresets();

        // Email + WhatsApp templates — Send Forms only shows the templates
        // an admin tagged for forms delivery.
        //   * Email: PracticeEngagementTemplate filtered by category=FORMS.
        //     Admins author these from the Engagement Messages tab and pick
        //     ``Forms`` from the standard category dropdown.
        //   * WhatsApp: WhatsappMessageTemplate filtered by usage_type=FORMS.
        //     Admins manage these from the WhatsApp Templates tab using the
        //     existing usage type field.
        // The wizard never invents categories or seeds rows — if no templates
        // are tagged ``FORMS``, the dropdown is empty and the wizard falls
        // back to the per-practice defaults configured in practice.settings.
        setTemplatesLoading(true);
        Promise.all([
            callApi(`/api/v1/practices/${practiceId}/engagement-templates/?channel__in=EMAIL&category=FORMS&use_unlayer=false&ordering=-created_at&limit=100`),
            callApi(`/api/v1/me/whatsapp-templates/?practice__in=${practiceId}&usage_type=FORMS&ordering=-created_at&limit=100`),
        ])
            .then(([emailRes, wpRes]) => {
                if (emailRes.status === 200 && emailRes.data?.results) {
                    setEmailTemplates(emailRes.data.results);
                }
                if (wpRes.status === 200 && wpRes.data?.results) {
                    setWhatsappTemplates(wpRes.data.results);
                }
            })
            .catch(() => {})
            .finally(() => setTemplatesLoading(false));
    }, [open, practiceId, dispatch]);

    // ── Default templates from practice.settings ────────────────────────────
    useEffect(() => {
        if (!open) return;
        const settings = practice?.settings;
        if (!settings) return;
        if (settings.consent_email_template && !emailTemplateId) {
            setEmailTemplateId(settings.consent_email_template);
        }
        if (settings.consent_whatsapp_template && !whatsappTemplateId) {
            setWhatsappTemplateId(settings.consent_whatsapp_template);
        }
    }, [open, practice]);

    // ── Fetch full template content when template id changes ────────────────
    useEffect(() => {
        if (!emailTemplateId || !practiceId) { setEmailTemplate(null); return; }
        callApi(`/api/v1/practices/${practiceId}/engagement-templates/${emailTemplateId}/`)
            .then((res) => {
                if (res.status === 200 && res.data) setEmailTemplate(res.data);
            })
            .catch(() => {});
    }, [emailTemplateId, practiceId]);

    useEffect(() => {
        if (!whatsappTemplateId || !practiceId) { setWhatsappTemplate(null); return; }
        // WhatsApp template detail also lives under /me/whatsapp-templates/,
        // not engagement-templates — see the list-fetch above.
        callApi(`/api/v1/me/whatsapp-templates/${whatsappTemplateId}/`)
            .then((res) => {
                if (res.status === 200 && res.data) setWhatsappTemplate(res.data);
            })
            .catch(() => {});
    }, [whatsappTemplateId, practiceId]);

    const loadPresets = useCallback(() => {
        if (!practiceId) return;
        callApi(`/api/v1/practices/${practiceId}/form-sequences/?limit=50`)
            .then((res) => {
                if (res.status === 200 && res.data?.results) {
                    setSequencePresets(res.data.results);
                }
            })
            .catch(() => {});
    }, [practiceId]);

    // ── Reset on close ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) {
            const t = setTimeout(() => {
                setAudienceMode('ONE_PATIENT');
                if (!initialLead) setSelectedLead(null);
                setSelectedPatients([]);
                setSelectedSegment(null);
                // broadcastAttested state removed; attestation reset above.
                setSelectedForms(initialForm ? [initialForm] : []);
                setSelectedTreatmentIds([]);
                setExtraTreatmentIds([]);
                setConsentConfig(null);
                setBundleMode(true);
                setExpiryHours(DEFAULT_EXPIRY_HOURS);
                setCustomHours(48);
                setChannel('EMAIL');
                // Clear template state too — without this, reopening the
                // dialog for a different practice bleeds the previous
                // tenant's template id/body into the new session and the
                // "Default templates from practice.settings" effect sees
                // a non-null id and skips its own fill.
                setEmailTemplateId(null);
                setWhatsappTemplateId(null);
                setEmailTemplate(null);
                setWhatsappTemplate(null);
                setEmailTemplates([]);
                setWhatsappTemplates([]);
                setTemplatesLoading(false);
                setBccEnabled(false);
                setBccList([]);
                setInlineEmailDraft(null);
                setInlineWhatsappDraft(null);
                setActivePresetId(null);
                setPresetSnapshot(null);
                setSending(false);
                setSendResult(null);
                setGenerating(false);
                setIdempotencyKey(uuidv4());
                setChannelStatus({ EMAIL: 'IDLE', WHATSAPP: 'IDLE' });
                setAttestation({ lawful_basis: '', source_description: '' });
                setPerFormTreatmentIds({});
                setPerFormPracticeFieldValues({});
                setRecurrence(null);
                setPracticeFieldValues({});
                setPreviewTemplateDraft(null);
                setScheduleMode('NOW');
                setScheduledAt('');
            }, 200);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [open]);

    // ── Form handlers ────────────────────────────────────────────────────────
    const handleAddForm = useCallback((form) => {
        setSelectedForms((prev) => (prev.find((f) => f.id === form.id) ? prev : [...prev, form]));
    }, []);

    const handleRemoveForm = useCallback((formId) => {
        setSelectedForms((prev) => prev.filter((f) => f.id !== formId));
    }, []);

    const handleReorderForms = useCallback((fromIndex, toIndex) => {
        setSelectedForms((prev) => {
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    }, []);

    const handleToggleTreatment = useCallback((id) => {
        setSelectedTreatmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }, []);

    const handleAddExtraTreatment = useCallback((id) => {
        setExtraTreatmentIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, []);

    const handleRemoveExtraTreatment = useCallback((id) => {
        setExtraTreatmentIds((prev) => prev.filter((x) => x !== id));
    }, []);

    // ── Preset handlers ──────────────────────────────────────────────────────
    const handleApplyPreset = useCallback((presetId) => {
        const preset = sequencePresets.find((p) => String(p.id) === String(presetId));
        if (!preset?.form_ids?.length) return;
        const formsList = forms?.results || [];
        const presetForms = preset.form_ids
            .map((fid) => formsList.find((f) => String(f.id) === String(fid)))
            .filter(Boolean);
        if (presetForms.length) {
            setSelectedForms(presetForms);
            setBundleMode(true);
            setActivePresetId(preset.id);
            setPresetSnapshot({
                form_ids: presetForms.map((f) => f.id),
            });
        }
    }, [sequencePresets, forms]);

    const handleSaveCurrentAsPreset = useCallback(() => {
        // Open the preset manager already in "create" mode, seeded with the
        // current selection.
        setPresetManagerView('create');
        setPresetManagerOpen(true);
    }, []);

    // Detect "unsaved changes" against the active preset
    const presetHasChanges = useMemo(() => {
        if (!activePresetId || !presetSnapshot) return false;
        const currentIds = selectedForms.map((f) => f.id);
        if (currentIds.length !== presetSnapshot.form_ids.length) return true;
        return currentIds.some((id, i) => id !== presetSnapshot.form_ids[i]);
    }, [activePresetId, presetSnapshot, selectedForms]);

    const handleSavePresetChanges = useCallback(async () => {
        if (!activePresetId || !practiceId) return;
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/form-sequences/${activePresetId}/`,
                'patch',
                { form_ids: selectedForms.map((f) => f.id) }
            );
            if (res.status === 200) {
                enqueueSnackbar('Preset updated', { variant: 'success' });
                setPresetSnapshot({ form_ids: selectedForms.map((f) => f.id) });
                loadPresets();
            } else {
                enqueueSnackbar('Failed to update preset', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to update preset', { variant: 'error' });
        }
    }, [activePresetId, practiceId, selectedForms, enqueueSnackbar, loadPresets]);

    // ── Validation ───────────────────────────────────────────────────────────
    const composeValid = useMemo(() => {
        if (selectedForms.length === 0) return false;
        if (channels.length === 0) return false;
        if (isOnePatient) {
            if (!selectedLead) return false;
            // Each selected outbound channel needs the matching contact field.
            // Copy/QR don't need contact info — they're surfaces, not deliveries.
            if (channels.includes('EMAIL') && !patientEmail) return false;
            if (channels.includes('WHATSAPP') && !patientPhone) return false;
        }
        if (isPatientList) {
            // Need either at least one patient or a saved segment.
            if (!selectedSegment && selectedPatients.length === 0) return false;
            // Proper GDPR attestation — replaces the old boolean
            // ``broadcastAttested`` which sent the wrong shape to the BE.
            if (!isAttestationValid(attestation)) return false;
        }
        // Schedule-later requires a future timestamp
        if (scheduleMode === 'LATER') {
            if (!scheduledAt) return false;
            const ts = new Date(scheduledAt);
            if (Number.isNaN(ts.getTime()) || ts.getTime() <= Date.now()) return false;
            // BE caps scheduled sends to ONE_PATIENT only in v1.
            if (!isOnePatient) return false;
        }
        // ANONYMOUS — only needs forms + at least one shareable channel
        // (matrix already restricts ANONYMOUS to COPY/QR upstream).
        return true;
    }, [
        selectedForms, channels, isOnePatient, isPatientList, selectedLead,
        patientEmail, patientPhone, selectedSegment, selectedPatients,
        attestation, scheduleMode, scheduledAt,
    ]);

    // Recipient count for the attestation card + PATIENT_LIST summary.
    const recipientCount = useMemo(() => {
        if (isPatientList) {
            if (selectedSegment) return selectedSegment.recipient_count || selectedSegment.size || 0;
            return selectedPatients.length;
        }
        if (isOnePatient && selectedLead) return 1;
        return 0;
    }, [isPatientList, isOnePatient, selectedSegment, selectedPatients, selectedLead]);

    // FE-4: single-screen layout — no Next/Back. Footer Send button
    // invokes handleFinalSend directly.
    const progress = 100;

    // ──────────────────────────────────────────────────────────────────────
    // Post-process dispatch helpers
    //
    // The Advanced view follows the same pattern Quick Send adopted:
    //   * ``handleGenerateLink`` runs the pre-flight POST that creates
    //     the ConsentRequest(s) and returns the shareable form URL.
    //   * ``handleSendChannel(channel)`` runs the per-channel dispatch,
    //     reusing the pre-flight CR via ``consent_request_ids``.
    //
    // PATIENT_LIST and ANONYMOUS sends are one-shot — the BE doesn't
    // expose multi-channel reuse for those modes — so we route them
    // through ``handleBroadcast`` below.
    // ──────────────────────────────────────────────────────────────────────

    /** Build the audience-mode-specific fragment of the payload. */
    const buildAudiencePayload = useCallback(() => {
        const fragment = {};
        if (isOnePatient && selectedLead) {
            fragment.lead_id = Number(selectedLead.id);
            const treatmentIds = Array.from(new Set([...selectedTreatmentIds, ...extraTreatmentIds]));
            if (treatmentIds.length) fragment.treatment_ids = treatmentIds;
        } else if (isPatientList) {
            if (selectedSegment) {
                fragment.audience_id = Number(selectedSegment.id);
            } else {
                fragment.lead_ids = selectedPatients.map((p) => Number(p.id));
            }
            fragment.broadcast_attestation = {
                lawful_basis: attestation.lawful_basis,
                source_description: (attestation.source_description || '').trim(),
            };
        }
        return fragment;
    }, [
        isOnePatient, isPatientList, selectedLead, selectedTreatmentIds, extraTreatmentIds,
        selectedSegment, selectedPatients, attestation,
    ]);

    /** Per-form overrides — strip empty entries before shipping. */
    const buildPerFormOverrides = useCallback(() => {
        const overrides = {};
        const tCleaned = Object.fromEntries(
            Object.entries(perFormTreatmentIds || {})
                .filter(([, v]) => Array.isArray(v) && v.length > 0)
                .map(([k, v]) => [String(k), v.map(Number)]),
        );
        if (Object.keys(tCleaned).length) overrides.per_form_treatment_ids = tCleaned;
        const pfCleaned = {};
        Object.entries(perFormPracticeFieldValues || {}).forEach(([k, v]) => {
            if (!v) return;
            const inner = Object.fromEntries(
                Object.entries(v).filter(([, rec]) => rec && rec.value !== '' && rec.value != null),
            );
            if (Object.keys(inner).length) pfCleaned[String(k)] = inner;
        });
        if (Object.keys(pfCleaned).length) overrides.per_form_practice_field_values = pfCleaned;
        return overrides;
    }, [perFormTreatmentIds, perFormPracticeFieldValues]);

    const hydrateSendResult = useCallback((resData) => {
        // ANONYMOUS responses use ``anonymous_links`` (the BE chose a
        // distinct top-level key for no-Lead sends). ONE_PATIENT /
        // PATIENT_LIST use ``consent_requests``. Both row shapes are
        // identical so we union them here — without this, anonymous
        // pre-flights succeed server-side but the FE banner reads
        // "Pre-flight didn't produce a link".
        const consentRequests = [
            ...(Array.isArray(resData?.consent_requests) ? resData.consent_requests : []),
            ...(Array.isArray(resData?.anonymous_links) ? resData.anonymous_links : []),
        ];
        const links = selectedForms.map((form, i) => {
            const cr = consentRequests.find((r) => Number(r?.form_id) === Number(form.id))
                || consentRequests[i]
                || null;
            return {
                form,
                accessToken: cr?.access_token || null,
                consentRequestId: cr?.id || null,
                url: cr?.form_url || '',
                expiresAt: cr?.expires_at || null,
            };
        });
        const earliestExpiry = links.map((l) => l.expiresAt).filter(Boolean).sort()[0] || null;
        setSendResult({
            links,
            channel,
            formUrl: links[0]?.url || '',
            recipientCount: resData?.recipient_count,
            taskDispatched: resData?.task_dispatched,
            expiresAt: earliestExpiry,
        });
        if (typeof onSuccess === 'function') {
            onSuccess({
                consentRequests: consentRequests
                    .map((cr) => ({ id: cr.id, lead_id: cr.lead_id, status: cr.status }))
                    .filter((cr) => cr.id),
            });
        }
        return links;
    }, [selectedForms, channel, onSuccess]);

    const surfaceError = useCallback((data, fallback) => {
        let detail = fallback;
        if (typeof data === 'string') detail = data;
        else if (typeof data?.detail === 'string') { ({ detail } = data); }
        else if (Array.isArray(data?.channels) && data.channels[0]) detail = data.channels[0];
        else if (Array.isArray(data?.bcc) && data.bcc[0]) detail = data.bcc[0];
        else if (Array.isArray(data?.broadcast_attestation) && data.broadcast_attestation[0]) detail = data.broadcast_attestation[0];
        else if (Array.isArray(data?.scheduled_at) && data.scheduled_at[0]) detail = data.scheduled_at[0];
        else if (Array.isArray(data?.recurrence) && data.recurrence[0]) detail = data.recurrence[0];
        enqueueSnackbar(detail, { variant: 'error' });
    }, [enqueueSnackbar]);

    /** Pre-flight: create the CR(s) with channel=COPY_LINK and capture the URL. */
    const handleGenerateLink = useCallback(async () => {
        if (generating || sending || !composeValid) return;
        setGenerating(true);
        try {
            const payload = buildSendFormsPayload({
                audienceMode,
                channels: ['COPY_LINK'],
                selectedForms,
                expiresInHours: resolvedExpiryHours,
                selectedLead: isOnePatient ? selectedLead : null,
                practiceFieldValues,
                idempotencyKey,
                highFidelityPdf: true,
            });
            // Audience-mode fragment + per-form overrides + recurrence.
            Object.assign(payload, buildAudiencePayload());
            Object.assign(payload, buildPerFormOverrides());
            if (scheduleMode === 'LATER' && scheduledAt) {
                const ts = new Date(scheduledAt);
                if (!Number.isNaN(ts.getTime()) && ts.getTime() > Date.now()) {
                    payload.scheduled_at = ts.toISOString();
                }
            }
            if (recurrence && Number(recurrence.count) >= 2 && isOnePatient && scheduleMode === 'LATER') {
                payload.recurrence = {
                    frequency: recurrence.frequency || 'WEEKLY',
                    interval: Math.max(1, Math.min(12, Number(recurrence.interval) || 1)),
                    count: Math.max(2, Math.min(12, Number(recurrence.count) || 2)),
                };
            }
            const res = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/send-forms/`,
                'post', payload,
            );
            const ok = res?.status === 200 || res?.status === 201 || res?.status === 202;
            if (ok) {
                hydrateSendResult(res.data);
                if (isPatientList) {
                    enqueueSnackbar(`Broadcast queued (${res.data?.recipient_count ?? '—'} recipients)`, { variant: 'success' });
                } else {
                    enqueueSnackbar('Link generated', { variant: 'success' });
                }
            } else {
                surfaceError(res?.data, 'Failed to generate link');
            }
        } catch {
            enqueueSnackbar('Failed to generate link', { variant: 'error' });
        }
        setGenerating(false);
    }, [
        generating, sending, composeValid, audienceMode, selectedForms,
        resolvedExpiryHours, isOnePatient, isPatientList, selectedLead,
        practiceFieldValues, idempotencyKey, buildAudiencePayload,
        buildPerFormOverrides, scheduleMode, scheduledAt, recurrence,
        practiceId, hydrateSendResult, surfaceError, enqueueSnackbar,
    ]);

    /** Per-channel dispatch — reuses pre-flight CR ids. */
    const handleSendChannel = useCallback(async (target) => {
        if (sending) return;
        if (!['EMAIL', 'WHATSAPP'].includes(target)) return;
        // ANONYMOUS + EMAIL/WHATSAPP needs ad-hoc recipient, which we don't
        // collect in Advanced today — gate to non-anonymous. ANONYMOUS users
        // hand the patient the link directly from the banner.
        if (audienceMode === 'ANONYMOUS') return;
        const links = sendResult?.links || [];
        const consentRequestIds = links.map((l) => l.consentRequestId).filter(Boolean);
        if (isOnePatient && !consentRequestIds.length) {
            enqueueSnackbar('Generate the link first', { variant: 'warning' });
            return;
        }
        setSending(true);
        setChannelStatus((prev) => ({ ...prev, [target]: 'SENDING' }));
        try {
            const sendKey = `${uuidv4()}-${target.toLowerCase()}`;
            const bcc = (target === 'EMAIL' && bccEnabled) ? bccList : null;
            const payload = buildSendFormsPayload({
                audienceMode,
                channels: [target],
                selectedForms,
                expiresInHours: resolvedExpiryHours,
                selectedLead: isOnePatient ? selectedLead : null,
                practiceFieldValues,
                emailTemplateId: target === 'EMAIL' ? emailTemplateId : null,
                whatsappTemplateId: target === 'WHATSAPP' ? whatsappTemplateId : null,
                bcc,
                idempotencyKey: sendKey,
                consentRequestIds: isOnePatient ? consentRequestIds : null,
                highFidelityPdf: true,
            });
            Object.assign(payload, buildAudiencePayload());
            Object.assign(payload, buildPerFormOverrides());
            if (scheduleMode === 'LATER' && scheduledAt) {
                const ts = new Date(scheduledAt);
                if (!Number.isNaN(ts.getTime()) && ts.getTime() > Date.now()) {
                    payload.scheduled_at = ts.toISOString();
                }
            }
            const res = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/send-forms/`,
                'post', payload,
            );
            const ok = res?.status === 200 || res?.status === 201 || res?.status === 202;
            if (ok) {
                // Hydrate again so any new links / ids surface (broadcast mode).
                hydrateSendResult(res.data);
                setChannelStatus((prev) => ({ ...prev, [target]: 'SENT' }));
                const label = target === 'EMAIL' ? 'Email' : 'WhatsApp';
                if (isPatientList) {
                    enqueueSnackbar(`${label} broadcast queued (${res.data?.recipient_count ?? '—'} recipients)`, { variant: 'success' });
                } else {
                    enqueueSnackbar(`${label} sent to ${patientName}`, { variant: 'success' });
                }
            } else {
                setChannelStatus((prev) => ({ ...prev, [target]: 'FAILED' }));
                surfaceError(res?.data, `Failed to send ${target.toLowerCase()}`);
            }
        } catch {
            setChannelStatus((prev) => ({ ...prev, [target]: 'FAILED' }));
            enqueueSnackbar(`Failed to send ${target.toLowerCase()}`, { variant: 'error' });
        }
        setSending(false);
    }, [
        sending, audienceMode, sendResult, isOnePatient, isPatientList,
        bccEnabled, bccList, selectedForms, resolvedExpiryHours, selectedLead,
        practiceFieldValues, emailTemplateId, whatsappTemplateId,
        buildAudiencePayload, buildPerFormOverrides, scheduleMode, scheduledAt,
        practiceId, hydrateSendResult, patientName, enqueueSnackbar, surfaceError,
    ]);

    /** Discard pre-flight CR + start over with a fresh idempotency key. */
    const handleResetSend = useCallback(() => {
        setSendResult(null);
        setChannelStatus({ EMAIL: 'IDLE', WHATSAPP: 'IDLE' });
        setIdempotencyKey(uuidv4());
    }, []);

    // The compose step (step 0) is a two-column layout that needs the lg
    // envelope. Step 1 — both the review summary and the post-send success
    // card — is a single column of summary content; reusing the lg width
    // for it leaves a sea of empty grey on every side. Shrink the dialog
    // for step 1 in either sub-state.
    // FE-4: legacy step-2 booleans kept as `false` so the existing
    // PaperProps / DialogContent sizing logic continues to work without
    // a wholesale rewrite. The single-screen layout always uses the
    // "compose" envelope.
    const isStep2 = false;
    // showingSuccess is no longer used — the wizard no longer renders
    // a success panel inside the dialog; the snackbar + closing the
    // dialog is enough feedback for the practice user.

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={viewMode === 'QUICK' ? 'xs' : 'lg'}
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'hidden',
                        // Quick mode: roughly matches the v1 Consent Builder
                        // envelope — wide enough for the email composer to
                        // breathe, narrower than Advanced's lg shell.
                        maxHeight: isMobile ? '100vh' : '92vh',
                        ...(viewMode === 'QUICK' && !isMobile && {
                            // Slightly wider envelope so the email composer
                            // and the Step 3 three-lane recipient selector
                            // breathe — 560 was tight enough that the modal
                            // header crowded the Quick/Advanced toggle.
                            maxWidth: '600px',
                            maxHeight: '90vh',
                        }),
                        boxShadow: '0 20px 60px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.06)',
                        // Smooth resize when transitioning between steps.
                        transition: 'max-width 250ms ease, max-height 250ms ease',
                        ...(isStep2 && !isMobile && {
                            // Override the lg envelope. ``maxHeight: 'none'``
                            // releases the 92vh cap so Paper sizes to its
                            // (now small) content rather than stretching.
                            // Review and success both fit comfortably in a
                            // single ~560px column; using the same width for
                            // both means no jarring resize after Generate Link.
                            maxWidth: '560px',
                            maxHeight: 'none',
                        }),
                    },
                }}
                TransitionComponent={Fade}
                transitionDuration={250}
            >
                {/* Progress bar */}
                <Box sx={{ height: 3, bgcolor: 'rgba(145,158,171,0.06)', position: 'relative' }}>
                    <Box
                        sx={{
                            position: 'absolute', top: 0, left: 0, height: '100%',
                            width: `${progress}%`,
                            bgcolor: common.mintyZest,
                            borderRadius: '0 4px 4px 0',
                            transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
                        }}
                    />
                </Box>

                {/* Header */}
                <DialogTitle
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider',
                    }}
                >
                    <Box
                        sx={{
                            width: 34, height: 34, borderRadius: '10px',
                            background: `linear-gradient(135deg, ${alpha(common.mintyZest, 0.15)}, ${alpha(common.mintyZest, 0.05)})`,
                            color: common.mintyZest,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Iconify icon="solar:document-bold" width={18} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3, color: '#1A2027' }}>
                            Forms / Consent
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11.5px' }}>
                            Send one or more forms to a patient
                        </Typography>
                    </Box>

                    {/* Quick ↔ Advanced segmented toggle.
                        Persisted to localStorage on change so a user who flipped
                        to Advanced last time lands there again. */}
                    <Box
                        role="tablist"
                        sx={{
                            display: 'inline-flex',
                            bgcolor: 'rgba(145,158,171,0.12)',
                            borderRadius: '999px',
                            p: '3px',
                            gap: '2px',
                            mr: 1,
                        }}
                    >
                        {['QUICK', 'ADVANCED'].map((mode) => {
                            const active = viewMode === mode;
                            return (
                                <Button
                                    key={mode}
                                    role="tab"
                                    aria-selected={active}
                                    onClick={() => setViewMode(mode)}
                                    size="small"
                                    disableRipple
                                    sx={{
                                        minWidth: 0,
                                        px: 1.5, py: 0.25,
                                        borderRadius: '999px',
                                        fontSize: '11px', fontWeight: 600,
                                        textTransform: 'none',
                                        color: active ? '#1C252E' : 'text.secondary',
                                        bgcolor: active ? '#fff' : 'transparent',
                                        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                        '&:hover': { bgcolor: active ? '#fff' : 'rgba(145,158,171,0.16)' },
                                    }}
                                >
                                    {mode === 'QUICK' ? 'Quick' : 'Advanced'}
                                </Button>
                            );
                        })}
                    </Box>

                    <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label="Close dialog"
                        sx={{
                            color: 'text.disabled', width: 28, height: 28, borderRadius: '8px',
                            '&:hover': { color: 'text.primary', bgcolor: 'rgba(145,158,171,0.08)' },
                        }}
                    >
                        <Iconify icon="eva:close-fill" width={16} />
                    </IconButton>
                </DialogTitle>

                {/* Content.
                    Default MUI DialogContent has ``flex: 1 1 auto``, which makes
                    it grow to fill the Paper's available height. That's correct
                    for the compose step (we want the two-column body to fill
                    the modal), but in success state we've shrunk Paper itself,
                    and we want DialogContent to size to its tiny success card
                    rather than stretch. ``flex: '0 0 auto'`` releases the grow. */}
                <DialogContent
                    sx={{
                        p: 0, overflow: 'auto', bgcolor: '#fafbfc',
                        ...(isStep2 && { flex: '0 0 auto' }),
                    }}
                >
                    {viewMode === 'QUICK' ? (
                        <QuickSend
                            open={open}
                            onClose={onClose}
                            practiceId={practiceId}
                            initialLead={initialLead}
                            switchToAdvanced={switchToAdvanced}
                            /* B-7: state lifted to this dialog so flipping
                               viewMode preserves the user's selections.
                               initialForm seeding now also happens here —
                               see the setSelectedForms effect at line ~217. */
                            selectedForms={selectedForms}
                            onAddForm={handleAddForm}
                            onRemoveForm={handleRemoveForm}
                            onReorderForms={handleReorderForms}
                            selectedLead={selectedLead}
                            onSelectLead={setSelectedLead}
                            practiceFieldValues={practiceFieldValues}
                            setPracticeFieldValues={setPracticeFieldValues}
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <ComposeStep
                            isMobile={isMobile}
                            audienceMode={audienceMode}
                            setAudienceMode={setAudienceMode}
                            practiceId={practiceId}
                            initialLead={initialLead}
                            selectedLead={selectedLead}
                            setSelectedLead={setSelectedLead}
                            selectedPatients={selectedPatients}
                            setSelectedPatients={setSelectedPatients}
                            selectedSegment={selectedSegment}
                            setSelectedSegment={setSelectedSegment}
                            attestation={attestation}
                            setAttestation={setAttestation}
                            recipientCount={recipientCount}
                            perFormTreatmentIds={perFormTreatmentIds}
                            setPerFormTreatmentIds={setPerFormTreatmentIds}
                            perFormPracticeFieldValues={perFormPracticeFieldValues}
                            setPerFormPracticeFieldValues={setPerFormPracticeFieldValues}
                            forms={forms}
                            formsLoading={formsLoading}
                            selectedForms={selectedForms}
                            onAddForm={handleAddForm}
                            onRemoveForm={handleRemoveForm}
                            onReorderForms={handleReorderForms}
                            patientTreatments={patientTreatments}
                            practiceTreatments={practiceTreatments}
                            selectedTreatmentIds={selectedTreatmentIds}
                            onToggleTreatment={handleToggleTreatment}
                            extraTreatmentIds={extraTreatmentIds}
                            onAddExtraTreatment={handleAddExtraTreatment}
                            onRemoveExtraTreatment={handleRemoveExtraTreatment}
                            sequencePresets={sequencePresets}
                            onApplyPreset={handleApplyPreset}
                            onManagePresets={() => { setPresetManagerView('list'); setPresetManagerOpen(true); }}
                            onSaveCurrentAsPreset={handleSaveCurrentAsPreset}
                            activePresetId={activePresetId}
                            presetHasChanges={presetHasChanges}
                            onSavePresetChanges={handleSavePresetChanges}
                            bundleMode={bundleMode}
                            setBundleMode={setBundleMode}
                            expiryHours={expiryHours}
                            setExpiryHours={setExpiryHours}
                            customHours={customHours}
                            setCustomHours={setCustomHours}
                            channel={channel}
                            setChannel={setChannel}
                            channels={channels}
                            setChannels={setChannels}
                            hasEmail={hasEmail}
                            hasWhatsApp={hasWhatsApp}
                            patientEmail={patientEmail}
                            patientPhone={patientPhone}
                            patientName={patientName}
                            practiceName={practiceName}
                            emailTemplateId={emailTemplateId}
                            setEmailTemplateId={setEmailTemplateId}
                            whatsappTemplateId={whatsappTemplateId}
                            setWhatsappTemplateId={setWhatsappTemplateId}
                            emailTemplates={emailTemplates}
                            whatsappTemplates={whatsappTemplates}
                            emailTemplate={emailTemplate}
                            whatsappTemplate={whatsappTemplate}
                            templatesLoading={templatesLoading}
                            bccEnabled={bccEnabled}
                            setBccEnabled={setBccEnabled}
                            bccList={bccList}
                            setBccList={setBccList}
                            inlineEmailDraft={inlineEmailDraft}
                            setInlineEmailDraft={setInlineEmailDraft}
                            inlineWhatsappDraft={inlineWhatsappDraft}
                            setInlineWhatsappDraft={setInlineWhatsappDraft}
                            consentConfig={consentConfig}
                            // FE-2 / FE-4 / FE-5 props
                            practiceFieldValues={practiceFieldValues}
                            setPracticeFieldValues={setPracticeFieldValues}
                            previewTemplateDraft={previewTemplateDraft}
                            onPreviewTemplateChange={handlePreviewTemplateChange}
                            scheduleMode={scheduleMode}
                            setScheduleMode={setScheduleMode}
                            expiryDays={expiryDays}
                            setExpiryDays={setExpiryDays}
                        />
                    )}
                </DialogContent>

                {/* Send settings strip — FE-7 correction 3: Audience /
                    Schedule / Link expiry as compact "label: value · Change"
                    pills directly above the Cancel/Send footer.
                    Hidden in QUICK mode — the wizard renders its own footer. */}
                {viewMode !== 'QUICK' && (
                <Box
                    sx={{
                        px: 2.5, py: 1, gap: 1,
                        borderTop: '1px solid', borderColor: 'divider',
                        bgcolor: '#fafbfc',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: { xs: 'flex-start', sm: 'space-between' },
                    }}
                >
                    <Box sx={{ fontSize: '11.5px', color: '#637381' }}>
                        <Box component="span" sx={{ fontWeight: 700, color: '#1C252E' }}>{selectedForms.length}</Box>{' '}
                        form{selectedForms.length === 1 ? '' : 's'}
                        <Box component="span" sx={{ mx: 0.5 }}>·</Box>
                        <Box component="span" sx={{ fontWeight: 700, color: '#1C252E' }}>{channels.length}</Box>{' '}
                        channel{channels.length === 1 ? '' : 's'}
                    </Box>
                    <SendSettingsStrip
                        audienceMode={audienceMode}
                        onAudienceModeChange={setAudienceMode}
                        audienceDisabled={Boolean(initialLead)}
                        scheduleMode={scheduleMode}
                        onScheduleModeChange={setScheduleMode}
                        scheduledAt={scheduledAt}
                        onScheduledAtChange={setScheduledAt}
                        expiryDays={expiryDays}
                        onExpiryDaysChange={setExpiryDays}
                        recurrence={recurrence}
                        onRecurrenceChange={setRecurrence}
                        recurrenceDisabled={!isOnePatient}
                        recurrenceDisabledReason="Recurring sends are only available for one-patient audience."
                    />
                </Box>
                )}

                {/* Post-process dispatch bar — only renders after a successful
                    pre-flight in Advanced mode. Hosts the link banner + the
                    per-channel Send pills that reuse the pre-flight CR via
                    consent_request_ids. */}
                {viewMode !== 'QUICK' && sendResult && (
                    <AdvancedDispatchBar
                        sendResult={sendResult}
                        channels={channels}
                        audienceMode={audienceMode}
                        selectedLead={selectedLead}
                        patientEmail={patientEmail}
                        patientPhone={patientPhone}
                        emailTemplateId={emailTemplateId}
                        whatsappTemplateId={whatsappTemplateId}
                        channelStatus={channelStatus}
                        onSendChannel={handleSendChannel}
                        onReset={handleResetSend}
                    />
                )}

                {/* Footer — Advanced now has a TWO-stage flow:
                    1. "Generate link" (pre-flight) — primary action while
                       composeValid && !sendResult.
                    2. Once the link exists, the dispatch bar above takes
                       over and the footer reduces to "Cancel / Done".
                    Hidden in QUICK mode — the wizard renders its own footer. */}
                {viewMode !== 'QUICK' && (
                <DialogActions
                    sx={{
                        px: 2.5, py: 1.5, gap: 1,
                        borderTop: '1px solid', borderColor: 'divider',
                        bgcolor: '#fafbfc',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={onClose}
                            disabled={sending || generating}
                            sx={{
                                borderRadius: '8px',
                                borderColor: 'rgba(145,158,171,0.24)',
                                color: 'text.secondary',
                                textTransform: 'none', fontWeight: 600, fontSize: '13px',
                                px: 2, py: 0.5, minHeight: 34,
                                '&:hover': { borderColor: 'rgba(145,158,171,0.4)', bgcolor: 'rgba(145,158,171,0.04)' },
                            }}
                        >
                            {sendResult ? 'Done' : 'Cancel'}
                        </Button>

                        {!sendResult && (
                            <Tooltip
                                title={!composeValid ? composeValidationMessage({
                                    selectedForms, audienceMode, selectedLead, selectedPatients,
                                    selectedSegment, attestation, channels, patientEmail, patientPhone,
                                    scheduleMode, scheduledAt,
                                }) : ''}
                                disableInteractive
                            >
                                <span>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!composeValid || generating || sending}
                                        onClick={handleGenerateLink}
                                        endIcon={generating
                                            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                            : <Iconify icon="eva:link-2-fill" width={16} />}
                                        sx={{
                                            borderRadius: '8px',
                                            bgcolor: common.mintyZest,
                                            '&:hover': { bgcolor: '#007B40' },
                                            fontWeight: 700, textTransform: 'none', fontSize: '13px',
                                            px: 2.5, py: 0.5, minHeight: 34,
                                            boxShadow: `0 3px 10px ${alpha(common.mintyZest, 0.25)}`,
                                            '&.Mui-disabled': {
                                                bgcolor: 'rgba(145,158,171,0.2)',
                                                color: '#919EAB',
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        {(() => {
                                            if (generating) return 'Generating…';
                                            if (isPatientList) return 'Queue broadcast';
                                            if (scheduleMode === 'LATER') return 'Schedule send';
                                            return 'Generate link';
                                        })()}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                </DialogActions>
                )}
            </Dialog>

            {/* Preset Manager */}
            <SequencePresetManager
                open={presetManagerOpen}
                onClose={() => { setPresetManagerOpen(false); setPresetManagerView('list'); }}
                practiceId={practiceId}
                sequencePresets={sequencePresets}
                forms={forms}
                selectedForms={selectedForms}
                onApplyPreset={handleApplyPreset}
                onPresetsChanged={loadPresets}
                initialView={presetManagerView}
            />
        </>
    );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const composeValidationMessage = ({
    selectedForms, audienceMode, selectedLead, selectedPatients, selectedSegment,
    attestation, channels, patientEmail, patientPhone,
    scheduleMode, scheduledAt,
}) => {
    if (selectedForms.length === 0) return 'Choose at least one form';
    if (!channels || channels.length === 0) return 'Pick at least one channel';
    if (audienceMode === 'ONE_PATIENT') {
        if (!selectedLead) return 'Select a patient';
        if (channels.includes('EMAIL') && !patientEmail) return 'This patient has no email on file';
        if (channels.includes('WHATSAPP') && !patientPhone) return 'This patient has no phone on file';
    }
    if (audienceMode === 'PATIENT_LIST') {
        if (!selectedSegment && selectedPatients.length === 0) return 'Pick at least one patient or a saved segment';
        if (!isAttestationValid(attestation)) return 'Complete the GDPR attestation (lawful basis + source)';
    }
    if (scheduleMode === 'LATER') {
        if (audienceMode !== 'ONE_PATIENT') return 'Scheduled sends are one-patient only in this iteration';
        if (!scheduledAt) return 'Pick a scheduled send time';
        const ts = new Date(scheduledAt);
        if (Number.isNaN(ts.getTime()) || ts.getTime() <= Date.now()) return 'Scheduled time must be in the future';
    }
    return '';
};

// SendForms's URL builder is a thin delegate around the shared utility
// in src/utils/resolvedDomainUrl.js. Local signature preserved so the
// downstream callers in this file continue to work unchanged.
//
// pathPrefix is 'forms' to match the FORMS_V2 DomainUseCase.path_prefix
// (apps/domains/migrations/0002_seed_registry_and_use_cases.py). The
// previous 'consent' value produced /consent/<token> links that 404'd
// on practices using the configured /forms/ path. This fallback only
// fires when the BE response did NOT include ``form_url``; in normal
// flow the BE-supplied URL (now also corrected via
// build_public_form_url → build_url_for) wins.
const buildFormUrl = (accessToken, consentConfig) =>
    buildResolvedDomainUrl({
        consentConfig,
        pathPrefix: 'forms',
        token: accessToken,
    });

// ─── Compose Step ───────────────────────────────────────────────────────────
//
// Layout: 2-column grid on desktop, stacks on mobile.
//   Left column  — three vertically stacked numbered sections:
//                    1. Audience
//                    2. Forms
//                    3. Channel & Message (includes link expiry)
//   Right column — sticky <SendFormsPreview /> rail.

const ComposeStep = ({
    isMobile, audienceMode, setAudienceMode,
    practiceId, initialLead, selectedLead, setSelectedLead,
    selectedPatients, setSelectedPatients,
    selectedSegment, setSelectedSegment,
    attestation, setAttestation, recipientCount,
    perFormTreatmentIds, setPerFormTreatmentIds,
    perFormPracticeFieldValues, setPerFormPracticeFieldValues,
    forms, formsLoading, selectedForms, onAddForm, onRemoveForm, onReorderForms,
    patientTreatments, practiceTreatments,
    selectedTreatmentIds, onToggleTreatment,
    extraTreatmentIds, onAddExtraTreatment, onRemoveExtraTreatment,
    sequencePresets, onApplyPreset, onManagePresets, onSaveCurrentAsPreset,
    activePresetId, presetHasChanges, onSavePresetChanges,
    bundleMode, setBundleMode,
    expiryHours, setExpiryHours, customHours, setCustomHours,
    channel, setChannel,
    channels, setChannels, hasEmail, hasWhatsApp,
    patientEmail, patientPhone, patientName, practiceName,
    emailTemplateId, setEmailTemplateId,
    whatsappTemplateId, setWhatsappTemplateId,
    emailTemplates, whatsappTemplates,
    emailTemplate, whatsappTemplate, templatesLoading,
    bccEnabled, setBccEnabled, bccList, setBccList,
    inlineEmailDraft, setInlineEmailDraft,
    inlineWhatsappDraft, setInlineWhatsappDraft,
    consentConfig,
    // FE-2: practice field values dict (system:treated_by + per-form fields)
    practiceFieldValues, setPracticeFieldValues,
    // FE-5: contenteditable draft from the preview panel
    previewTemplateDraft, onPreviewTemplateChange,
    // FE-4: schedule mode + expiry in days for the AdvancedReveal accordion
    scheduleMode, setScheduleMode,
    expiryDays, setExpiryDays,
}) => {
    const isAnonymous = audienceMode === 'ANONYMOUS';
    const isOnePatient = audienceMode === 'ONE_PATIENT';
    const isPatientList = audienceMode === 'PATIENT_LIST';

    // Form-type drives template filtering. Use the FIRST selected form's
    // type — mixed-type sequences are an edge case the user can refine
    // by editing subject/body inline.
    const formType = selectedForms?.[0]?.form_type || null;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.55fr) minmax(0, 1fr)',
                gap: 0,
                minHeight: isMobile ? 'auto' : 600,
            }}
        >
            {/* ── LEFT COLUMN: 3 numbered sections (Audience → Forms → Channel) ─ */}
            <Box
                sx={{
                    p: 2.5,
                    borderRight: isMobile ? 'none' : '1px solid',
                    borderBottom: isMobile ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                }}
            >
                {/* ─────────── Section 1 — RECIPIENT ───────────
                    FE-7: the audience-mode toggle (One patient / Patient list /
                    Anonymous) has moved to the SendSettingsStrip above the
                    footer. Section 1 just shows the context-appropriate
                    recipient picker for the currently-active audience mode. */}
                <Section
                    number={1}
                    title="Recipient"
                    subtitle="Who should receive these forms?"
                >
                    {initialLead ? (
                        <Box
                            sx={{
                                p: 1.25, borderRadius: '10px',
                                bgcolor: alpha(common.mintyZest, 0.04),
                                border: '1px solid', borderColor: alpha(common.mintyZest, 0.15),
                                display: 'flex', alignItems: 'center', gap: 1,
                            }}
                        >
                            <Iconify icon="solar:user-bold" width={16} sx={{ color: common.mintyZest }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 700 }} noWrap>
                                    {patientName}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }} noWrap>
                                    {patientEmail || patientPhone || 'No contact info'}
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                label="Pinned"
                                sx={{
                                    height: 20, fontSize: '10px', fontWeight: 700,
                                    bgcolor: alpha(common.mintyZest, 0.1),
                                    color: common.mintyZest, textTransform: 'uppercase', letterSpacing: '0.4px',
                                }}
                            />
                        </Box>
                    ) : (
                        <>
                            {isOnePatient && (
                                <PatientSelector
                                    practiceId={practiceId}
                                    selectedLead={selectedLead}
                                    onSelect={setSelectedLead}
                                />
                            )}

                            {isPatientList && (
                                <Stack spacing={1.5}>
                                    <MultiPatientPicker
                                        practiceId={practiceId}
                                        selectedPatients={selectedPatients}
                                        onPatientsChange={setSelectedPatients}
                                        selectedSegment={selectedSegment}
                                        onSegmentChange={setSelectedSegment}
                                    />
                                    {/* Proper GDPR attestation — lawful_basis +
                                        source_description. Replaces the legacy
                                        boolean switch which sent a non-conformant
                                        shape and 400'd every PATIENT_LIST send. */}
                                    <BroadcastAttestationCard
                                        value={attestation}
                                        onChange={setAttestation}
                                        recipientCount={recipientCount}
                                    />
                                </Stack>
                            )}

                            {isAnonymous && (
                                <Box
                                    sx={{
                                        px: 1.5, py: 1.25, borderRadius: '10px',
                                        bgcolor: alpha('#078DEE', 0.04),
                                        border: '1px solid', borderColor: alpha('#078DEE', 0.15),
                                        display: 'flex', gap: 1, alignItems: 'flex-start',
                                    }}
                                >
                                    <Iconify icon="eva:info-fill" width={16} sx={{ color: '#078DEE', mt: '2px', flexShrink: 0 }} />
                                    <Typography variant="caption" sx={{ fontSize: '11.5px', color: '#637381', lineHeight: 1.5 }}>
                                        You&apos;ll generate a public link that anyone can access. Best suited to surveys.
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Section>

                {/* ─────────── Section 2 — FORMS ───────────
                    FE-7 correction 1: the default is now a SINGLE-FORM
                    picker. For multi-form sequences the user opens the
                    Advanced accordion below. */}
                <Section number={2} title="Forms" subtitle="Pick a form. Need more than one? Open Advanced.">
                    {/* Treatment context (ONE_PATIENT mode only, when patient selected) */}
                    {isOnePatient && selectedLead && (patientTreatments.length > 0 || practiceTreatments.length > 0) && (
                        <Box sx={{ mb: 1.5 }}>
                            <SectionLabel sx={{ mb: 1 }}>Treatments</SectionLabel>
                            <TreatmentSelector
                                treatments={patientTreatments}
                                selectedTreatmentIds={selectedTreatmentIds}
                                onToggle={onToggleTreatment}
                                practiceTreatments={practiceTreatments}
                                extraTreatmentIds={extraTreatmentIds}
                                onAddExtra={onAddExtraTreatment}
                                onRemoveExtra={onRemoveExtraTreatment}
                            />
                        </Box>
                    )}

                    {activePresetId && presetHasChanges && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, mb: 0.75 }}>
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                px: 0.75, py: 0.25, borderRadius: '6px',
                                bgcolor: alpha('#FFAB00', 0.1),
                                color: '#B76E00',
                                fontSize: '10px', fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.4px',
                            }}>
                                <Iconify icon="eva:alert-circle-fill" width={12} />
                                Unsaved
                            </Box>
                            <Button
                                size="small"
                                onClick={onSavePresetChanges}
                                startIcon={<Iconify icon="eva:save-fill" width={12} />}
                                sx={{
                                    fontSize: '10.5px', fontWeight: 600,
                                    color: common.mintyZest, textTransform: 'none',
                                    py: 0.25, minHeight: 0, px: 0.75,
                                    '&:hover': { bgcolor: alpha(common.mintyZest, 0.06) },
                                }}
                            >
                                Save preset changes
                            </Button>
                        </Box>
                    )}

                    <SingleFormPicker
                        forms={forms}
                        formsLoading={formsLoading}
                        selectedForms={selectedForms}
                        onAddForm={onAddForm}
                        onRemoveForm={onRemoveForm}
                        practiceId={practiceId}
                        sequencePresets={sequencePresets}
                        onApplyPreset={onApplyPreset}
                        onManagePresets={onManagePresets}
                        onSaveCurrentAsPreset={onSaveCurrentAsPreset}
                    />

                    {/* Smart suggestions removed per user feedback — kept the
                        import + component file alive for potential future
                        reinstatement, but not rendered. The single-form picker
                        is intentionally lean. */}

                    {/* Bundle toggle */}
                    {isOnePatient && selectedForms.length > 1 && (
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={bundleMode}
                                        onChange={(e) => setBundleMode(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: common.mintyZest },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: common.mintyZest },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600 }}>
                                        Send as one sequence link
                                    </Typography>
                                }
                                sx={{ ml: 0 }}
                            />
                        </Box>
                    )}
                </Section>

                {/* ─── ADVANCED — multi-form sequence builder ───
                    FE-7 correction 2: this accordion is now SPECIFICALLY
                    for sequences. Audience / Schedule / Link expiry have
                    moved to the SendSettingsStrip above the wizard footer. */}
                <AdvancedReveal
                    /* Default-expanded inside the Advanced view — the
                       sequence builder is the primary surface here, not
                       a hidden affordance. In Quick mode this accordion
                       isn't rendered at all (Quick has its own step). */
                    defaultExpanded
                    forms={forms}
                    formsLoading={formsLoading}
                    selectedForms={selectedForms}
                    onAddForm={onAddForm}
                    onRemoveForm={onRemoveForm}
                    onReorderForms={onReorderForms}
                    practiceId={practiceId}
                    sequencePresets={sequencePresets}
                    onApplyPreset={onApplyPreset}
                    onManagePresets={onManagePresets}
                    onSaveCurrentAsPreset={onSaveCurrentAsPreset}
                    treatmentOptions={[
                        ...patientTreatments.map((t) => ({ id: t.id, name: t.name, category: t.category })),
                        ...practiceTreatments
                            .filter((pt) => !patientTreatments.find((t) => t.id === pt.id))
                            .map((t) => ({ id: t.id, name: t.name, category: t.category })),
                    ]}
                    practitionerOptions={[]}
                    perFormTreatmentIds={perFormTreatmentIds}
                    onPerFormTreatmentChange={(formId, ids) => setPerFormTreatmentIds((prev) => {
                        const next = { ...prev };
                        const key = String(formId);
                        if (!ids || ids.length === 0) {
                            delete next[key];
                        } else {
                            next[key] = ids;
                        }
                        return next;
                    })}
                    perFormPracticeFieldValues={perFormPracticeFieldValues}
                    onPerFormPracticeFieldChange={(formId, vals) => setPerFormPracticeFieldValues((prev) => {
                        const next = { ...prev };
                        const key = String(formId);
                        const cleaned = Object.fromEntries(
                            Object.entries(vals || {}).filter(([, rec]) => rec && rec.value !== '' && rec.value != null),
                        );
                        if (Object.keys(cleaned).length === 0) {
                            delete next[key];
                        } else {
                            next[key] = cleaned;
                        }
                        return next;
                    })}
                />

                {/* ─── PRACTICE FIELDS ───
                    FE-2 spec: Treated by is always rendered as a system
                    default; template-declared practice fields enumerate
                    on top of that. */}
                <Section number={3} title="Practice fields" subtitle="Set by you, attached to the PDF. Patient won't see fields marked hidden.">
                    <PracticeFieldsBlock
                        selectedForms={selectedForms}
                        selectedLead={selectedLead}
                        practiceName={practiceName}
                        initialValues={practiceFieldValues}
                        onChange={setPracticeFieldValues}
                    />
                </Section>

                {/* ─────────── Section 4 — CHANNEL & MESSAGE ─────────── */}
                <Section number={4} title="Channel & Message" subtitle="How should we deliver it?">
                    <ChannelComposer
                        isAnonymous={isAnonymous}
                        audienceMode={audienceMode}
                        channels={channels}
                        setChannels={setChannels}
                        patientEmail={patientEmail}
                        patientPhone={patientPhone}
                        emailTemplateId={emailTemplateId}
                        setEmailTemplateId={setEmailTemplateId}
                        whatsappTemplateId={whatsappTemplateId}
                        setWhatsappTemplateId={setWhatsappTemplateId}
                        emailTemplates={emailTemplates}
                        whatsappTemplates={whatsappTemplates}
                        templatesLoading={templatesLoading}
                        bccEnabled={bccEnabled}
                        setBccEnabled={setBccEnabled}
                        bccList={bccList}
                        setBccList={setBccList}
                        // The Track E form-type-aware ``TemplateCard`` was
                        // retired on 2026-04-27. Admins now tag templates
                        // with ``category=FORMS`` (email) and
                        // ``usage_type=FORMS`` (WhatsApp); the regular
                        // dropdown rendered by ``ChannelComposer`` already
                        // shows only those — no special inline slot needed.
                        inlineTemplateSlot={null}
                    />
                    {/* Link expiry has moved to SendSettingsStrip above
                        the wizard footer (FE-7 correction 3). */}
                </Section>
            </Box>

            {/* ── RIGHT COLUMN: sticky preview rail ──────────────────────────
                The rail is now the *only* preview surface — the in-line
                Preview stack inside ChannelComposer was removed in the
                same change that introduced this multi-channel render
                because the two pipelines disagreed on merge-tag
                resolution and HTML rendering.

                The rail receives the full ``channels`` array (so it can
                stack one mockup per selected channel), the actual
                ``emailTemplate`` / ``whatsappTemplate`` (so HTML email
                bodies render correctly via dangerouslySetInnerHTML), and
                ``consentConfig`` (so the URL it shows matches the
                practice's branded-subdomain configuration). */}
            <Box sx={{ p: 2, bgcolor: '#fafbfc' }}>
                <SendFormsPreview
                    audienceMode={audienceMode}
                    selectedPatient={selectedLead}
                    selectedPatients={selectedPatients}
                    selectedForms={selectedForms}
                    channels={channels}
                    channel={channel === 'COPY' ? 'COPY_LINK' : channel}
                    templateState={previewTemplateDraft}
                    emailTemplate={emailTemplate}
                    whatsappTemplate={whatsappTemplate}
                    expiryHours={expiryHours === 'custom' ? Math.max(1, Number(customHours) || 1) : Number(expiryHours)}
                    practiceName={practiceName}
                    consentConfig={consentConfig}
                    practiceFieldValues={practiceFieldValues}
                    onTemplateChange={onPreviewTemplateChange}
                />
            </Box>
        </Box>
    );
};

ComposeStep.propTypes = {
    isMobile: PropTypes.bool,
    audienceMode: PropTypes.oneOf(['ONE_PATIENT', 'PATIENT_LIST', 'ANONYMOUS']),
    setAudienceMode: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialLead: PropTypes.object,
    selectedLead: PropTypes.object,
    setSelectedLead: PropTypes.func,
    selectedPatients: PropTypes.array,
    setSelectedPatients: PropTypes.func,
    selectedSegment: PropTypes.object,
    setSelectedSegment: PropTypes.func,
    attestation: PropTypes.shape({
        lawful_basis: PropTypes.string,
        source_description: PropTypes.string,
    }),
    setAttestation: PropTypes.func,
    recipientCount: PropTypes.number,
    perFormTreatmentIds: PropTypes.object,
    setPerFormTreatmentIds: PropTypes.func,
    perFormPracticeFieldValues: PropTypes.object,
    setPerFormPracticeFieldValues: PropTypes.func,
    forms: PropTypes.object,
    formsLoading: PropTypes.bool,
    selectedForms: PropTypes.array,
    onAddForm: PropTypes.func,
    onRemoveForm: PropTypes.func,
    onReorderForms: PropTypes.func,
    patientTreatments: PropTypes.array,
    practiceTreatments: PropTypes.array,
    selectedTreatmentIds: PropTypes.array,
    onToggleTreatment: PropTypes.func,
    extraTreatmentIds: PropTypes.array,
    onAddExtraTreatment: PropTypes.func,
    onRemoveExtraTreatment: PropTypes.func,
    sequencePresets: PropTypes.array,
    onApplyPreset: PropTypes.func,
    onManagePresets: PropTypes.func,
    onSaveCurrentAsPreset: PropTypes.func,
    activePresetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    presetHasChanges: PropTypes.bool,
    onSavePresetChanges: PropTypes.func,
    bundleMode: PropTypes.bool,
    setBundleMode: PropTypes.func,
    expiryHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setExpiryHours: PropTypes.func,
    customHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setCustomHours: PropTypes.func,
    channel: PropTypes.string,
    setChannel: PropTypes.func,
    channels: PropTypes.arrayOf(PropTypes.string),
    setChannels: PropTypes.func,
    hasEmail: PropTypes.bool,
    hasWhatsApp: PropTypes.bool,
    patientEmail: PropTypes.string,
    patientPhone: PropTypes.string,
    patientName: PropTypes.string,
    practiceName: PropTypes.string,
    emailTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setEmailTemplateId: PropTypes.func,
    whatsappTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setWhatsappTemplateId: PropTypes.func,
    emailTemplates: PropTypes.array,
    whatsappTemplates: PropTypes.array,
    emailTemplate: PropTypes.object,
    whatsappTemplate: PropTypes.object,
    templatesLoading: PropTypes.bool,
    bccEnabled: PropTypes.bool,
    setBccEnabled: PropTypes.func,
    bccList: PropTypes.array,
    setBccList: PropTypes.func,
    inlineEmailDraft: PropTypes.object,
    setInlineEmailDraft: PropTypes.func,
    inlineWhatsappDraft: PropTypes.object,
    setInlineWhatsappDraft: PropTypes.func,
    consentConfig: PropTypes.object,
    practiceFieldValues: PropTypes.object,
    setPracticeFieldValues: PropTypes.func,
    previewTemplateDraft: PropTypes.object,
    onPreviewTemplateChange: PropTypes.func,
    scheduleMode: PropTypes.oneOf(['NOW', 'LATER']),
    setScheduleMode: PropTypes.func,
    expiryDays: PropTypes.number,
    setExpiryDays: PropTypes.func,
};

// ─── Tiny presentational helpers ────────────────────────────────────────────

const Section = ({ number, title, subtitle, children }) => (
    <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <Box
                sx={{
                    width: 22, height: 22, borderRadius: '50%',
                    bgcolor: alpha(common.mintyZest, 0.12),
                    color: common.mintyZest,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 800,
                    flexShrink: 0,
                }}
            >
                {number}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '13px', color: '#212B36', lineHeight: 1.2 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.2 }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
        {children}
    </Box>
);
Section.propTypes = {
    number: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    children: PropTypes.node,
};

const SectionLabel = ({ children, sx = {} }) => (
    <Typography
        variant="subtitle2"
        sx={{
            fontWeight: 700,
            fontSize: '10.5px',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            ...sx,
        }}
    >
        {children}
    </Typography>
);
SectionLabel.propTypes = { children: PropTypes.node, sx: PropTypes.object };

// ─── Main exports ───────────────────────────────────────────────────────────

SendFormsDialog.propTypes = {
    open:        PropTypes.bool.isRequired,
    onClose:     PropTypes.func.isRequired,
    practiceId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    initialLead: PropTypes.object,
    initialForm: PropTypes.object,
    onSuccess:   PropTypes.func,
};

SendFormsDialog.defaultProps = {
    initialLead: null,
    initialForm: null,
    onSuccess:   null,
};

export default SendFormsDialog;
